<script setup>
import MarketGroupCard from '@/components/Market/MarketGroupCard.vue';
import { timestampToDayMonth, timestampToTime } from '@/helpers';
import { useFixturesStore } from '@/stores/fixtures';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch , onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { socket } from '@/feed.socket';
import { useBetslipStore } from '@/stores/betslip';
import _ from 'lodash';
import HShirt from '@/components/HShirt.vue';
import AShirt from '@/components/AShirt.vue';

const assetsUrl = import.meta.env.VITE_ASSETS_URL
const router = useRouter()
const route = useRoute()
const fixtures = useFixturesStore()
const betslips = useBetslipStore()
const { marketGroups } = storeToRefs(fixtures)
const { isEventSelected } = storeToRefs(betslips)

const event = ref({})
const { selections } = storeToRefs(fixtures)
const activeGroup = ref(null)
const homeKitError = ref(false)
const awayKitError = ref(false)
const eid = computed(() => route.params.id)
const dayOfMatch = computed(() => timestampToDayMonth(event.value.t))

const goBack = () => router.go(-1)

const groupsOfEvent = computed(() => {
  if (!selections.value.length || !marketGroups.value.length || !event.value.si) return []

  const eventSel = selections.value.find(s => Number(s.fi) === Number(eid.value))
  if (!eventSel || !Array.isArray(eventSel.m)) return []

  const eventMarketIds = new Set(eventSel.m.map(m => Number(m.i)))

  return marketGroups.value
    .filter(group =>
      Number(group.si) === Number(event.value.si) &&
      group.mp.some(marketId => eventMarketIds.has(marketId))
    )
    .sort((a, b) => (a.s ?? 9999) - (b.s ?? 9999))
})

watch(groupsOfEvent, (groups) => {
  if (!activeGroup.value && groups.length) {
    activeGroup.value = groups[0].i
  }
})

const eventMarketsByActiveGroup = computed(() => {
  if (!activeGroup.value || !selections.value.length) return []

  const eventSel = selections.value.find(s => Number(s.fi) === Number(eid.value))
  if (!eventSel || !Array.isArray(eventSel.m)) return []

  const group = groupsOfEvent.value.find(g => g.i == activeGroup.value)
  if (!group) return []
return eventSel.m
    .filter(market => group.mp.includes(Number(market.i)))
    .map(market => {
      const fullMarket = fixtures.allMarkets.find(am => am.i === Number(market.i))
      return {
        ...market,
        sortOrder: fullMarket?.s ?? 9999,
        nrsValue: Array.isArray(market.nrs) ? Number(market.nrs[0]) : 9999
      }
    })
    .sort((a, b) => {
      const sort = a.sortOrder - b.sortOrder
      if (sort !== 0) return sort

      return a.nrsValue - b.nrsValue
    })
})
onMounted(async () => {
  await fixtures.fetchGroups()
  event.value = fixtures.getEvent(eid.value)

  await fixtures.fetchEventSelection(eid.value, event.value?.s)
    socket.emit('fixture-odds', {i: eid.value})
})
onBeforeUnmount(async () => {
  if (!isEventSelected.value(event.value.i)) {
    socket.emit('leave-fixture-odds', {i: event.value.i})  
  }
})
</script>

<template>
  <div class="flex" v-if="event">
    <div class="flex-1 overflow-hidden">
      <div class="flex w-full relative items-center">
        <div class="evp-EventHeader">
          <div class="px-8 relative">
            <div class="pt-4">
              <p @click="goBack" class="text-white text-xs cursor-pointer">
                &lt; {{ $te(`sports.${event.si}`) ? $t(`sports.${event.si}`) : event?.sn }} - {{ event?.cn }}
              </p>
            </div>

            <div class="py-6 flex justify-between space-x-4" v-if="event.o == 'false'">
              <div class="flex-1 flex flex-col justify-center">
                <div class="flex flex-col items-center">
                  <div v-if="!homeKitError">
                    <img
                      v-if="event?.hi"
                      :src="`${assetsUrl}/teams/${event.hi}.png`"
                      class="w-[40px] h-[40px] object-contain"
                      @error="homeKitError = true"
                    />
                  </div>
                  <div v-else class="w-[40px] h-[40px]">
                    <HShirt />
                  </div>
                  <p class="evp-Participant">{{ event.h }}</p>
                </div>
              </div>

              <div class="text-white text-xs text-center" v-if="event.t">
                <p>{{ timestampToTime(event.t) }}</p>
                <p>{{ dayOfMatch[0] }} {{ dayOfMatch[1] }} {{ dayOfMatch[2] }}</p>
              </div>

              <div class="flex-1 flex flex-col justify-center">
                <div class="flex flex-col items-center">
                  <div v-if="!awayKitError">
                    <img
                      v-if="event?.ai"
                      :src="`${assetsUrl}/teams/${event.ai}.png`"
                      class="w-[40px] h-[40px] object-contain"
                      @error="awayKitError = true"
                    />
                  </div>
                  <div v-else class="w-[40px] h-[40px]">
                    <AShirt />
                  </div>
                  <p class="evp-Participant">{{ event.a }}</p>
                </div>
              </div>
            </div>

            <div class="py-6 flex justify-between space-x-4" v-if="event.o == 'true'">
              <div class="flex-1 flex flex-col justify-center">
                <p class="evp-Participant">{{ $t('events.outrightf') }}</p>
              </div>
            </div>

            <div class="pb-4">
              <ul class="list-none flex space-x-4 overflow-x-auto">
                <li v-for="group in groupsOfEvent" :key="group.i">
                  <div
                    class="text-[13px] text-white cursor-pointer px-2 py-1"
                    :class="{
                      'bg-[var(--color-primary3)] rounded-2xl !text-black font-bold':
                        group.i == activeGroup,
                    }"
                    @click="activeGroup = group.i"
                  >
                    {{ $te(`marketsGroups.${group.n}`) ?  $t(`marketsGroups.${group.n}`) :  group.n}}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="py-1.5 xl:py-4 px-2 xl:px-6 bg-[var(--color-grey3)] relative" v-if="activeGroup">
        <template v-for="market in eventMarketsByActiveGroup" :key="market.ui">
          <MarketGroupCard
            :market="market"
            :odds="(market?.o || []).slice().sort((a,b) => (a.s ?? 9999) - (b.s ?? 9999))"
            :event="event"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.evp-EventHeader {
  @apply flex flex-col w-full border-b;
}
.evp-EventHeader::before {
  background-image: url('/soccer-bg.webp');
  height: 100%;
  content: "";
  position: absolute;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50%;
  border-bottom: 2px solid var(--color-primary2);
}
.evp-Participant {
  @apply text-white font-bold text-center pt-2;
  font-size: 15px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
}
</style>
